'use client';

import { useLayoutEffect, useRef, useState } from 'react';

/**
 * Row content for a single search hit. Shows the register suffix only when
 * the name + suffix fit on one line without truncating the name. The decision
 * is made by rendering an invisible probe that holds the full natural-width
 * content and comparing its width against the row's available width via a
 * ResizeObserver — so the suffix appears/disappears as the dropdown resizes.
 */
/** A row of /api/companies/suggest — what the dropdown shows and links to. */
export interface SuggestRow {
  euId: string;
  name: string;
  city: string | null;
  registerId: string | null;
  registerType: string | null;
  registerCourt: string | null;
  faviconUrl: string | null;
}

export function ResultRowContent({
  name,
  suffix,
}: {
  name: string;
  suffix: string | null;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const probeRef = useRef<HTMLSpanElement>(null);
  const [showSuffix, setShowSuffix] = useState(false);

  useLayoutEffect(() => {
    if (!suffix) {
      return;
    }
    const container = containerRef.current;
    const probe = probeRef.current;
    if (!container || !probe) return;
    const measure = () => {
      setShowSuffix(probe.scrollWidth <= container.clientWidth);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(container);
    return () => ro.disconnect();
  }, [name, suffix]);

  return (
    <div
      ref={containerRef}
      className="relative flex items-baseline gap-1 min-w-0 flex-1 overflow-hidden"
    >
      <span className="text-sm truncate text-left">{name}</span>
      {showSuffix && suffix && (
        <span className="text-xs text-muted-foreground shrink-0">
          · {suffix}
        </span>
      )}
      {suffix && (
        <span
          ref={probeRef}
          aria-hidden
          className="invisible absolute left-0 top-0 whitespace-nowrap pointer-events-none"
        >
          <span className="text-sm">{name}</span>
          <span className="text-xs"> · {suffix}</span>
        </span>
      )}
    </div>
  );
}
